import { useEffect, useRef } from 'react';
import { notification } from 'antd';
import { getAIPOTaskStatus } from '../modules/Inventory/_redux/purchase-orders/purchaseOrdersCrud';

export const useGlobalAITaskRecovery = () => {
  const hasChecked = useRef(false);

  useEffect(() => {
    // Prevent multiple checks in the same session
    if (hasChecked.current) {
      return;
    }

    const safeCheck = async () => {
      try {
        // Skip while the user isn't authenticated yet (e.g. still sitting on the
        // login page) - getAIPOTaskStatus is a protected endpoint and firing it
        // here without a token in Redux just produces a 401/403 toast.
        if (window.location.pathname.startsWith('/auth')) {
          hasChecked.current = true;
          return;
        }

        const recentTaskId = localStorage.getItem('ai_po_task_id');
        const taskStartTime = localStorage.getItem('ai_po_task_start_time');
        const notificationShown = localStorage.getItem('ai_po_notification_shown');

        console.log('Checking task:', { recentTaskId, notificationShown }); // Debug log

        if (!recentTaskId || !taskStartTime) {
          hasChecked.current = true;
          return;
        }

        const startTime = new Date(taskStartTime);
        const now = new Date();
        const timeDiff = (now - startTime) / (1000 * 60);

        // Clean up old tasks (older than 30 minutes)
        if (timeDiff > 30) {
          localStorage.removeItem('ai_po_task_id');
          localStorage.removeItem('ai_po_task_start_time');
          localStorage.removeItem('ai_po_notification_shown');
          hasChecked.current = true;
          return;
        }

        // Only proceed if notification hasn't been shown
        if (!notificationShown) {
          console.log('Fetching task status for:', recentTaskId); // Debug log

          try {
            const response = await getAIPOTaskStatus(recentTaskId);
            console.log('Task status response:', response); // Debug log

            // Handle TaskManager response structure
            let task = null;
            if (response.data && response.data.entity) {
              task = response.data.entity;
            } else if (response.data && response.data.task) {
              task = response.data.task;
            } else if (response.data) {
              task = response.data;
            }

            console.log('Parsed task:', task); // Debug log

            // Check if task is completed
            if (task && (task.status === 3 || task.result?.success !== undefined)) {
              const result = task.result;
              console.log('Task result:', result); // Debug log

              if (result && result.success && result.po_created && result.purchase_order_no) {
                // SUCCESS: Show PO creation notification with PO number
                notification.success({
                  key: 'ai-task-completion',
                  message: "AI Analysis Completed Successfully",
                  description: `Purchase Order #${result.purchase_order_no} was created while you were away and you can find it in the list.`,
                  placement: "topRight",
                  duration: 10,
                  style: { zIndex: 10000 }
                });

                console.log('Shown success notification for PO:', result.purchase_order_no);

              } else if (result && result.success === false && result.products_analyzed === 0) {
                // NO PRODUCTS: Show no products found notification
                notification.warning({
                  key: 'ai-task-completion',
                  message: "AI Analysis Complete",
                  description: "No products were found for the selected criteria while you were away.",
                  placement: "topRight",
                  duration: 10,
                  style: { zIndex: 10000 }
                });

                console.log('Shown no products notification');

              } else if (result && result.success === false) {
                // ERROR: Show error notification
                notification.error({
                  key: 'ai-task-completion',
                  message: "AI Analysis Failed",
                  description: "An error occurred during AI analysis while you were away.",
                  placement: "topRight",
                  duration: 10,
                  style: { zIndex: 10000 }
                });

                console.log('Shown error notification');
              }

              // Mark notification as shown to prevent duplicate notifications
              localStorage.setItem('ai_po_notification_shown', 'true');

              // Clean up task data after successful notification
              setTimeout(() => {
                localStorage.removeItem('ai_po_task_id');
                localStorage.removeItem('ai_po_task_start_time');
                localStorage.removeItem('ai_po_notification_shown');
                console.log('Cleaned up localStorage');
              }, 2000);

            } else {
              console.log('Task not yet completed, status:', task?.status);
              // Task not completed yet - don't show notification
            }

          } catch (apiError) {
            console.error("Error fetching task status:", apiError);

            // If task not found (404), it might be very old - clean up
            if (apiError.response?.status === 404) {
              localStorage.removeItem('ai_po_task_id');
              localStorage.removeItem('ai_po_task_start_time');
              localStorage.removeItem('ai_po_notification_shown');
              console.log('Task not found, cleaned up localStorage');
            }
          }
        } else {
          console.log('Notification already shown, skipping');
        }

        hasChecked.current = true;

      } catch (error) {
        console.error("Global AI task recovery error:", error);
        hasChecked.current = true;

        // Clean up on unexpected error
        try {
          localStorage.removeItem('ai_po_task_id');
          localStorage.removeItem('ai_po_task_start_time');
          localStorage.removeItem('ai_po_notification_shown');
        } catch (e) {
          console.error("Error cleaning localStorage:", e);
        }
      }
    };

    // Check after a short delay to ensure app is fully loaded
    const timeout = setTimeout(safeCheck, 3000);

    return () => clearTimeout(timeout);
  }, []); // Empty dependency array - only run once per component mount
};